import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw, XCircle } from "lucide-react";
/**
 * Detalle de comprobante con datos, ítems, totales y acciones (anular, reconsultar).
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { formatApiError } from "../api/client";
import {
  type EstadoComprobante,
  anularComprobante,
  consultarEstadoComprobante,
  getComprobante,
} from "../api/facturacion";
import { useAuth } from "../auth/AuthProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";

const ESTADO_VARIANT: Record<EstadoComprobante, "neutral" | "success" | "warning" | "danger"> = {
  PENDIENTE: "warning",
  ACEPTADO: "success",
  RECHAZADO: "danger",
  ANULADO: "neutral",
};

export function ComprobanteDetailPage() {
  const tz = useZonaHoraria();
  const { id } = useParams<{ slug: string; id: string }>();
  const base = useBase();
  const comprobanteId = Number(id);
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [anularOpen, setAnularOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["comprobante", comprobanteId],
    queryFn: () => getComprobante(comprobanteId),
    enabled: Number.isFinite(comprobanteId),
  });

  const anular = useMutation({
    mutationFn: (motivo: string) => anularComprobante(comprobanteId, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comprobante", comprobanteId] });
      queryClient.invalidateQueries({ queryKey: ["comprobantes"] });
      setAnularOpen(false);
      setMotivo("");
      setError(null);
    },
    onError: (err) => setError(formatApiError(err, "No se pudo anular.")),
  });

  const reconsultar = useMutation({
    mutationFn: () => consultarEstadoComprobante(comprobanteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["comprobante", comprobanteId] }),
  });

  if (query.isLoading) return <div className="text-sm text-slate-500">Cargando…</div>;
  if (query.error || !query.data) {
    return <Alert>No se pudo cargar el comprobante. Volvé y reintentá.</Alert>;
  }

  const c = query.data;
  const puedeAnular = c.estado === "ACEPTADO" && me?.permisos.includes("comprobante:anular");

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/facturacion`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a facturación
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900 font-mono">{c.numero_completo}</h1>
            <Badge variant={ESTADO_VARIANT[c.estado]}>{c.estado}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {c.tipo_comprobante} · emitido{" "}
            {fechaHora(c.fecha_emision, tz)}{" "}
            · provider {c.provider}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => reconsultar.mutate()}
            loading={reconsultar.isPending}
          >
            <RefreshCw className="w-4 h-4" />
            Reconsultar estado
          </Button>
          {puedeAnular && (
            <Button variant="danger" onClick={() => setAnularOpen(true)}>
              <XCircle className="w-4 h-4" />
              Anular
            </Button>
          )}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}

      {c.estado === "ANULADO" && c.motivo_anulacion && (
        <Alert variant="info">
          Anulado el{" "}
          {c.anulado_en &&
            fechaHora(c.anulado_en, tz)}{" "}
          — motivo: {c.motivo_anulacion}
        </Alert>
      )}

      {c.estado === "RECHAZADO" && c.error_mensaje && (
        <Alert>El OSE rechazó el comprobante: {c.error_mensaje}</Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-slate-900">Cliente</h2>
          </CardHeader>
          <CardBody className="text-sm space-y-1">
            <div>
              <span className="text-slate-500">Documento:</span>{" "}
              <span className="font-medium">
                {c.cliente_tipo_doc} {c.cliente_numero_doc ?? "—"}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Razón social:</span>{" "}
              <span className="font-medium">{c.cliente_razon_social}</span>
            </div>
            {c.cliente_direccion && (
              <div>
                <span className="text-slate-500">Dirección:</span> {c.cliente_direccion}
              </div>
            )}
            {c.cliente_email && (
              <div>
                <span className="text-slate-500">Email:</span> {c.cliente_email}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-slate-900">Totales</h2>
          </CardHeader>
          <CardBody className="text-sm space-y-1">
            <Row label="Gravado" value={c.subtotal_gravado} moneda={c.moneda} />
            <Row label="Exonerado" value={c.subtotal_exonerado} moneda={c.moneda} />
            <Row label="Inafecto" value={c.subtotal_inafecto} moneda={c.moneda} />
            <Row label="IGV" value={c.igv} moneda={c.moneda} />
            <div className="pt-2 border-t border-slate-200">
              <Row label="Total" value={c.total} moneda={c.moneda} bold />
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-slate-900">Ítems ({c.items.length})</h2>
        </CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">#</th>
                <th className="text-left px-4 py-2 font-medium">Descripción</th>
                <th className="text-right px-4 py-2 font-medium">Cant.</th>
                <th className="text-right px-4 py-2 font-medium">P. unit.</th>
                <th className="text-center px-4 py-2 font-medium">IGV</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {c.items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-500">{it.orden}</td>
                  <td className="px-4 py-2 text-slate-800">{it.descripcion}</td>
                  <td className="px-4 py-2 text-right">{it.cantidad}</td>
                  <td className="px-4 py-2 text-right">{it.precio_unitario}</td>
                  <td className="px-4 py-2 text-center text-xs text-slate-500">
                    {it.tipo_afectacion_igv}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">
                    {c.moneda === "PEN" ? "S/" : "$"} {it.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Dialog
        open={anularOpen}
        onClose={() => {
          setAnularOpen(false);
          setError(null);
        }}
        title="Anular comprobante"
        description="Una vez anulado se notifica al OSE. La operación no se puede revertir."
      >
        <div className="space-y-4">
          <Field
            label="Motivo de anulación"
            htmlFor="motivo"
            hint="Mínimo 3 caracteres. Aparecerá en la comunicación a SUNAT."
          >
            <Input
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Error en datos del cliente"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setAnularOpen(false);
                setError(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => anular.mutate(motivo.trim())}
              loading={anular.isPending}
              disabled={motivo.trim().length < 3}
            >
              Anular comprobante
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function Row({
  label,
  value,
  moneda,
  bold,
}: {
  label: string;
  value: string;
  moneda: string;
  bold?: boolean;
}) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-slate-900" : ""}`}>
      <span className={bold ? "" : "text-slate-500"}>{label}</span>
      <span>
        {moneda === "PEN" ? "S/" : "$"} {value}
      </span>
    </div>
  );
}
